/**
 * 1.7.7 — the calendar's fields, shared by New event, Edit event and the event's page (D-1.7.7-1, D-1.7.7-2).
 *
 *   ReminderChoiceFields   "Reminders for this event: Default / Off / Custom". Default says what the default is in
 *                          words (from Settings → Events and RSVPs); Custom is up to three rows of days before + a
 *                          time, and to whom. It only describes a choice: the save that follows is the press that
 *                          schedules the reminders (D-1.7.7-0) — nothing here sends or schedules anything.
 *   RoomField              the place of an event in the building: the rooms of Settings, plus Other… (a text box).
 *   SelectT / TextT / SwitchT   the app's fields (`@/features/people/fields`) with a test id on the control.
 *
 * And the words and the arithmetic the sheets share: `reminderWords`, `remindersAhead`, `repeatWords`.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MAX_DAYS_BEFORE, MAX_REMINDER_TIMES, clock, effectiveReminders, reminderDayKey, type ReminderAudience, type ReminderChoice, type ReminderSettings, type ReminderTime } from '@shared/event-calendar';
import type { EventKindChoice } from '@shared/event-cards';
import { dayOfWeek } from '@shared/dates';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';

/* ------------------------------------------------------------------------------------------------
 * Words and arithmetic
 * ---------------------------------------------------------------------------------------------- */

/** "2 days before" / "the day of". */
export const daysBeforeWords = (t: TFunction, daysBefore: number) => (daysBefore === 0 ? t('cal177n.rem.dayOf') : t('cal177n.rem.daysBefore', { count: daysBefore }));

const joinWords = (t: TFunction, parts: string[]) => (parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')}${t('cal177n.rem.and')}${parts[parts.length - 1]}`);

/** "2 days before at 10:00 AM and the day of at 9:00 AM, to all tenants with an email". */
export function reminderWords(t: TFunction, r: Pick<ReminderSettings, 'times' | 'audience'>): string {
  const times = joinWords(
    t,
    r.times.map((x) =>
      t('cal177n.rem.at', {
        when: daysBeforeWords(t, x.daysBefore),
        time: clock(x.minutes),
      }),
    ),
  );
  return t('cal177n.rem.words', {
    times,
    to: t(`cal177n.rem.to.${r.audience}`),
  });
}

/**
 * How many of an event's reminder times are still ahead: after now and before the event starts (the server's rule —
 * a 9:00 AM reminder for an 8:00 AM event is not sent). `now` is the New York day and minute.
 */
export function remindersAhead(times: readonly ReminderTime[], eventDayKey: string, eventMinutes: number, now: { dayKey: string; minutes: number }): number {
  const stamp = (dayKey: string, minutes: number) => `${dayKey}${String(minutes).padStart(4, '0')}`;
  const from = stamp(now.dayKey, now.minutes);
  const start = stamp(eventDayKey, eventMinutes);
  return times.filter((x) => {
    const at = stamp(reminderDayKey(eventDayKey, x), x.minutes);
    return at > from && at < start;
  }).length;
}

/** What a save would schedule: the effective times still ahead, over every day the save makes. */
export function remindersToSchedule(settings: ReminderSettings, choice: ReminderChoice, hasRsvp: boolean, days: readonly string[], eventMinutes: number, now: { dayKey: string; minutes: number }): number {
  const eff = effectiveReminders(settings, choice, hasRsvp);
  if (!eff.on || eff.audience === 'nobody') return 0;
  return days.reduce((n, d) => n + remindersAhead(eff.times, d, eventMinutes, now), 0);
}

/** "the 2nd Tuesday" — which one, from the chosen day. */
export function nthWeekdayWords(t: TFunction, dayKey: string): string {
  const n = Math.floor((Number(dayKey.slice(6, 8)) - 1) / 7) + 1;
  return t('cal177n.repeat.nth', {
    nth: t(`cal177n.repeat.ord.${n}`),
    weekday: t(`cal177n.repeat.weekday.${dayOfWeek(dayKey)}`),
  });
}

/** Every half hour from 6:00 AM to 9:00 PM — the times a reminder can leave. */
export const REMINDER_CLOCK = Array.from({ length: 31 }, (_, i) => 6 * 60 + i * 30);

/* ------------------------------------------------------------------------------------------------
 * Fields with a test id on the control
 * ---------------------------------------------------------------------------------------------- */

interface Base {
  id: string;
  testId?: string;
  label: ReactNode;
  help?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

function Req({ required }: { required?: boolean }) {
  const { t } = useTranslation();
  if (!required) return null;
  return (
    <span className="req">
      {' '}
      <span aria-hidden="true">*</span>
      <span className="sr-only">{t('people.required')}</span>
    </span>
  );
}

export function SelectT({
  id,
  testId,
  label,
  help,
  required,
  disabled,
  value,
  onChange,
  options,
  blank,
}: Base & {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  blank?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        <Req required={required} />
      </label>
      <select id={id} className="input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} aria-required={required || undefined} aria-describedby={help ? `${id}-help` : undefined} data-testid={testId ?? id}>
        {blank !== undefined ? <option value="">{blank}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {help ? (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

export function TextT({
  id,
  testId,
  label,
  help,
  required,
  disabled,
  value,
  onChange,
  type = 'text',
  inputMode,
  maxLength,
  placeholder,
}: Base & {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: 'text' | 'numeric';
  maxLength?: number;
  placeholder?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        <Req required={required} />
      </label>
      <input id={id} className="input" type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} inputMode={inputMode} maxLength={maxLength} placeholder={placeholder} autoComplete="off" aria-required={required || undefined} aria-describedby={help ? `${id}-help` : undefined} data-testid={testId ?? id} />
      {help ? (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

export function SwitchT({
  id,
  testId,
  label,
  help,
  disabled,
  checked,
  onChange,
}: Omit<Base, 'required'> & {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="field">
      <label className="switch" htmlFor={id}>
        <input id={id} type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={help ? `${id}-help` : undefined} data-testid={testId ?? id} />
        <span>{label}</span>
      </label>
      {help ? (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The place: a room of the building, or Other…
 * ---------------------------------------------------------------------------------------------- */

export const OTHER_ROOM = '__other';

/** The room the sheet opens on: the auditorium when the list has one, else the first room. */
export const firstRoom = (rooms: readonly string[]) => rooms.find((r) => /auditorium/i.test(r)) ?? rooms[0] ?? '';

/**
 * The place of an event in the building. `value` is the room's name; `other` says the name is typed (Other…), which
 * shows the text box. The select keeps the old field's id; the box has its own.
 */
export function RoomField({ id, testId, otherId, otherTestId, rooms, value, other, onChange, required }: { id: string; testId?: string; otherId: string; otherTestId?: string; rooms: readonly string[]; value: string; other: boolean; onChange: (value: string, other: boolean) => void; required?: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <SelectT id={id} testId={testId} label={t('cal177n.place')} required={required} value={other ? OTHER_ROOM : value} onChange={(v) => (v === OTHER_ROOM ? onChange('', true) : onChange(v, false))} options={[...rooms.map((r) => ({ value: r, label: r })), { value: OTHER_ROOM, label: t('cal177n.placeOther') }]} help={t('cal177n.placeHelp')} />
      {other ? <TextT id={otherId} testId={otherTestId} label={t('cal177n.placeOtherBox')} required={required} value={value} onChange={(v) => onChange(v, true)} maxLength={120} /> : null}
    </>
  );
}

/* ------------------------------------------------------------------------------------------------
 * 1.7.9 — What kind of event? (New event's first step, and the top of Edit)
 * ---------------------------------------------------------------------------------------------- */

export interface KindChoiceFieldsProps {
  /** The start of every id and test id: `ne` → `ne-kind-calendar`, `ne-kind-rsvp`, `ne-kind-volunteers`. */
  idPrefix: string;
  /** null = not chosen yet (New event asks; nothing is assumed). */
  answer: EventKindChoice['answer'] | 'workshop' | null;
  volunteers: boolean;
  onAnswer: (a: EventKindChoice['answer'] | 'workshop') => void;
  onVolunteers: (v: boolean) => void;
  /** New event only: a workshop is the third answer (it has its own short form). */
  workshop?: boolean;
  /** Why "On the calendar only" cannot be chosen (a raffle needs its RSVPs), or undefined. */
  calendarOff?: string;
  /** The question, when the sheet has not said it already in a heading. */
  legend?: string;
  /** The id of the heading that asks the question (New event's step heading). */
  labelledBy?: string;
}

/**
 * One question: on the calendar only · people answer (RSVP) · [a workshop] — and a tick, Needs volunteers, that goes
 * with either. What the kind shows and fixes is decided by `shared/event-cards.ts`, never here.
 */
export function KindChoiceFields({ idPrefix, answer, volunteers, onAnswer, onVolunteers, workshop = false, calendarOff, legend, labelledBy }: KindChoiceFieldsProps) {
  const { t } = useTranslation();
  const p = idPrefix;
  const answers: (EventKindChoice['answer'] | 'workshop')[] = workshop ? ['calendar', 'rsvp', 'workshop'] : ['calendar', 'rsvp'];
  return (
    <fieldset className="field ev179r-kind" aria-labelledby={legend ? undefined : labelledBy} data-testid={`${p}-kind-choice`}>
      {legend ? <legend className="eyebrow">{legend}</legend> : null}
      <div className="radio-cards">
        {answers.map((a) => {
          const off = a === 'calendar' && !!calendarOff;
          return (
            <label key={a} className={off ? 'is-off' : undefined}>
              <input type="radio" name={`${p}-kind-answer`} value={a} checked={answer === a} disabled={off} onChange={() => onAnswer(a)} aria-describedby={`${p}-kind-${a}-help`} data-testid={a === 'workshop' ? 'ev-type-workshop' : `${p}-kind-${a}`} />
              <span>
                {t(`ev179r.kind.${a}`)}
                <small id={`${p}-kind-${a}-help`}>{off ? calendarOff : t(`ev179r.kind.${a}Help`)}</small>
              </span>
            </label>
          );
        })}
      </div>
      <label className="ev179r-tick" htmlFor={`${p}-kind-volunteers`}>
        <input id={`${p}-kind-volunteers`} type="checkbox" checked={volunteers && answer !== 'workshop'} disabled={answer === 'workshop'} onChange={(e) => onVolunteers(e.target.checked)} aria-describedby={`${p}-kind-volunteers-help`} data-testid={`${p}-kind-volunteers`} />
        <span>
          {t('ev179r.kind.volunteers')}
          <small id={`${p}-kind-volunteers-help`}>{answer === 'workshop' ? t('ev179r.kind.volunteersWorkshop') : t('ev179r.kind.volunteersHelp')}</small>
        </span>
      </label>
    </fieldset>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Reminders for this event
 * ---------------------------------------------------------------------------------------------- */

const MODES = ['default', 'off', 'custom'] as const;
const AUDIENCE_ORDER: readonly ReminderAudience[] = ['all', 'yes', 'nobody'];

export interface ReminderChoiceFieldsProps {
  value: ReminderChoice;
  onChange: (c: ReminderChoice) => void;
  /** Settings → Events and RSVPs: whether reminders are on at all, when, to whom. */
  defaults: ReminderSettings;
  /** The event has RSVP on ("only those who said yes" needs one). */
  hasRsvp: boolean;
  /** The start of every id and test id (default `ne`: `ne-reminders`, `ne-reminders-custom`, `ne-reminder-days-0` …). */
  idPrefix?: string;
}

export function ReminderChoiceFields({ value, onChange, defaults, hasRsvp, idPrefix = 'ne' }: ReminderChoiceFieldsProps) {
  const { t } = useTranslation();
  const p = idPrefix;
  const times: ReminderTime[] = value.mode === 'custom' ? (value.times ?? []) : [];
  const audience: ReminderAudience = (value.mode === 'custom' ? value.audience : undefined) ?? defaults.audience;
  const custom = (next: ReminderTime[], to: ReminderAudience = audience) => onChange({ mode: 'custom', times: next, audience: to });
  const setMode = (m: (typeof MODES)[number]) => {
    if (m === value.mode) return;
    if (m === 'custom') return custom(defaults.times.length ? defaults.times.slice(0, MAX_REMINDER_TIMES).map((x) => ({ ...x })) : [{ daysBefore: 1, minutes: 10 * 60 }], defaults.audience);
    onChange({ mode: m });
  };
  const setTime = (i: number, patch: Partial<ReminderTime>) => custom(times.map((x, n) => (n === i ? { ...x, ...patch } : x)));
  const dayOptions = Array.from({ length: MAX_DAYS_BEFORE + 1 }, (_, d) => ({
    value: String(d),
    label: daysBeforeWords(t, d),
  }));
  const clockOptions = (minutes: number) => [...new Set([...REMINDER_CLOCK, minutes])].sort((a, b) => a - b).map((m) => ({ value: String(m), label: clock(m) }));
  const helpId = `${p}-reminders-help`;

  return (
    <fieldset className="field choice cal177n-rem" aria-describedby={helpId} data-testid={`${p}-reminders`}>
      <legend>{t('cal177n.rem.legend')}</legend>
      <div className="seg radios">
        {MODES.map((m) => (
          <label key={m} className={value.mode === m ? 'on' : undefined}>
            <input type="radio" name={`${p}-reminders`} value={m} checked={value.mode === m} onChange={() => setMode(m)} data-testid={`${p}-reminders-${m}`} />
            <span>{t(`cal177n.rem.mode.${m}`)}</span>
          </label>
        ))}
      </div>
      <div id={helpId} data-testid={`${p}-reminders-words`}>
        {!defaults.on ? (
          <p className="help cal177n-line">
            <Icon name="alert" width={16} height={16} />
            <span>{t('cal177n.rem.settingsOff')}</span>
          </p>
        ) : value.mode === 'default' ? (
          <p className="help">
            {t('cal177n.rem.defaultIs', {
              words: reminderWords(t, {
                times: defaults.times,
                audience: defaults.audience === 'yes' && !hasRsvp ? 'all' : defaults.audience,
              }),
            })}
          </p>
        ) : value.mode === 'off' ? (
          <p className="help">{t('cal177n.rem.offHelp')}</p>
        ) : (
          <p className="help">{t('cal177n.rem.customHelp', { max: MAX_REMINDER_TIMES })}</p>
        )}
      </div>
      {value.mode === 'custom' ? (
        <div className="cal177n-rem-rows">
          {times.map((x, i) => (
            <div className="cal177n-rem-row" key={i} role="group" aria-label={t('cal177n.rem.row', { n: i + 1 })} data-testid={`${p}-reminder-row-${i}`}>
              <SelectT id={`${p}-reminder-days-${i}`} label={t('cal177n.rem.days', { n: i + 1 })} value={String(x.daysBefore)} onChange={(v) => setTime(i, { daysBefore: Number(v) })} options={dayOptions} />
              <SelectT id={`${p}-reminder-time-${i}`} label={t('cal177n.rem.time', { n: i + 1 })} value={String(x.minutes)} onChange={(v) => setTime(i, { minutes: Number(v) })} options={clockOptions(x.minutes)} />
              <Button variant="secondary" size="sm" disabled={times.length <= 1} onClick={() => custom(times.filter((_, n) => n !== i))} aria-label={t('cal177n.rem.remove', { n: i + 1 })} data-testid={`${p}-reminder-remove-${i}`}>
                {t('cal177n.rem.removeShort')}
              </Button>
            </div>
          ))}
          {times.length < MAX_REMINDER_TIMES ? (
            <div>
              <Button variant="secondary" size="sm" onClick={() => custom([...times, { daysBefore: 1, minutes: 10 * 60 }])} data-testid={`${p}-reminder-add`}>
                {t('cal177n.rem.add')}
              </Button>
            </div>
          ) : null}
          <SelectT
            id={`${p}-reminder-audience`}
            label={t('cal177n.rem.audience')}
            value={audience}
            onChange={(v) => custom(times, v as ReminderAudience)}
            options={AUDIENCE_ORDER.map((a) => ({
              value: a,
              label: t(`cal177n.rem.aud.${a}`),
            }))}
            help={audience === 'yes' && !hasRsvp ? t('cal177n.rem.yesNoRsvp') : undefined}
          />
        </div>
      ) : null}
    </fieldset>
  );
}

/**
 * 26 Oct 2026 (D-1026-HOUSEHOLD): "How a household is counted" — the whole household, or limited tickets (N per household).
 * One control for Edit and New event; `prefix` keeps each sheet's ids ("ee" / "ne").
 */
export function CountChoiceFields({ prefix, mode, onMode, tickets, onTickets }: { prefix: string; mode: 'household' | 'limited'; onMode: (m: 'household' | 'limited') => void; tickets: string; onTickets: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className="field" aria-describedby={`${prefix}-count-help`} data-testid={`${prefix}-count`}>
      <legend className="eyebrow">{t('ev1026.edit.legend')}</legend>
      <div className="radio-cards">
        <label>
          <input type="radio" name={`${prefix}-count`} value="household" checked={mode === 'household'} onChange={() => onMode('household')} data-testid={`${prefix}-count-household`} />
          <span>{t('ev1026.edit.whole')}</span>
        </label>
        <label>
          <input type="radio" name={`${prefix}-count`} value="limited" checked={mode === 'limited'} onChange={() => onMode('limited')} data-testid={`${prefix}-count-limited`} />
          <span>{t('ev1026.edit.limited')}</span>
        </label>
      </div>
      {mode === 'limited' ? <TextT id={`${prefix}-tickets`} label={t('ev1026.edit.tickets')} inputMode="numeric" value={tickets} onChange={onTickets} maxLength={2} /> : null}
      <p className="help" id={`${prefix}-count-help`}>
        {mode === 'household' ? t('ev1026.edit.wholeHelp') : t('ev1026.edit.limitedHelp')}
      </p>
    </fieldset>
  );
}
